import { useAppConfig } from "../AppConfigProvider";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import withAuth from "../hocs/withAuth";
import noAuth from "../hocs/noAuth";
import { useMemo, useRef } from "react";
import { routingInstrumentation } from "../utils/sentry";
import { useAuth } from "../hooks/useAuth";
import { UserRoleUI } from "../contexts/AuthContext";
import { StyleSheet, View } from "react-native";
import Loader from "../components/common/status/Loader/Loader";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";
import type { InitialChatMessage } from "../containers/companion/types";

export type RootStackParamList = {
  Home: undefined;
  "Companion Chat": { chatId?: string; initialMessage?: InitialChatMessage };
  "Companion Home": undefined;
  Productivity: { tab?: string };
  Portfolio: undefined;
  "Student Analytics": undefined;
  Insights: undefined;
  Journal: undefined;
  Coursework: undefined;
  Activities: undefined;
  School: { integration_id: string };
  "Course Catalogue": { integration_id: string };
  BoxSchool: { integration_id: string };
  Course: undefined;
  "Custom Course Student": {
    courseId?: string;
    integration_id?: string;
    returnToCourseEditor?: boolean;
  };
  "Custom Module Student": {
    module_id: string;
    course_id: string;
    integration_id: string;
  };
  Career: undefined;
  Degree: undefined;
  Content: undefined;
  ContentForm: undefined;
  Project: {
    id: string | null;
    courseId?: string;
    integrationId?: string;
    initialStep?: string;
  };
  CareerDetails: undefined;
  Resource: {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
  };
  "Custom Resource Student": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Assignment Student": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Course AITutor": {
    resource_type: string;
    resource_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  "Custom Course Assessment": {
    assessment_id: string;
    course_id: string;
    integration_id: string;
    module_id?: string;
  };
  Learn: undefined;
  Categories: undefined;
  Tracks: {
    field: string;
    subfield: string;
  };
  "Track Overview": undefined;
  Lesson: {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
  };
  "Lesson Activities": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Assessment": {
    field: string;
    subfield: string;
    topic: string;
    chapter: string;
    subchapter: string;
    loTitle: string;
    loId: string;
  };
  "Lesson Chat": { loId: string };
  "Lesson Video": undefined;
  Onboarding: undefined;
  Settings: undefined;
  Faculty: {
    courseName: string;
    courseId: string;
    integration_id: string;
  };
  Invite: undefined;
  Login: { isLMS: boolean };
  LoginSuperAdmin: { isLMS: boolean };
  LoginLTI: { accessToken?: string; refreshToken?: string; redirect?: string };
  LoginSSO: { accessToken?: string; refreshToken?: string; redirect?: string };
  Signup: { isLMS: boolean };
  "Reset Password": { token?: string };
  "Set Password": {
    token?: string;
    f?: string;
    uid?: string;
    service?: string;
  };
  "Verify Email": undefined;
  ThankYou: undefined;
  EdlinkSuccess: undefined;
  "Custom School Student Courses": { integration_id: string };
  "Custom School Faculty Courses": { integration_id: string };
  "Custom Coursework Modules": {
    courseId: string;
    aiTutorIndex?: number;
    pageIndex?: number;
    moduleIndex?: number;
    courseName?: string;
    parentReturnTo?: string;
    returnTo?: string;
  };
  Admin: undefined;
  "Create Tenant": undefined;
  "Tenant Dashboard": undefined;
  "Tenant Settings": { id: string };
  "OAuth Client Dashboard": undefined;
  Assessment: { assessmentId: string; courseId: string; integrationId: string };
  "Course Settings": { courseId: string; tab?: string };
  // Admin Pages
  "Admin Dashboard": undefined;
  "AI Companion Dashboard": undefined;
  "Manage Courses": undefined;
  Teaching: undefined;
  "Degrees & Programs": undefined;
  "Course Information": {
    // admin dashboard course details page
    courseId: string;
    courseName: string;
    returnTo: string;
    activeTab?: string;
  };
  "Degree Information": {
    // admin dashboard degree details page
    degreeId: string;
    degreeName: string;
    returnTo?: string;
    activeTab?: string;
  };
  Students: undefined;
  "Student Profile": {
    studentId: string;
    studentName?: string;
    returnTo?: string;
  };
  "Admin Users": undefined;
  "Admin Grading Center": { submissionId?: string };
  "Admin Announcements": undefined;
  "Admin Analytics": { studentId?: string };
  "Admin Settings": undefined;
  "Admin Profile": undefined;
  "Session Analytics": undefined;
  "User Session Details": { userId: string };
  "Course Evaluator": undefined;
  "Course Evaluator Details": { courseId: string };
  Announcement: undefined;
  "Announcement Detail": {
    announcementId: string;
  };
  Enrollment: { invite?: string };
  "Chat Settings": undefined;
  "To do": undefined;
  "Learning Activities": undefined;
  "Interactive Learning Environment": {
    activityId?: string;
  };
};

type RouteName = keyof RootStackParamList;

const Stack = createStackNavigator<RootStackParamList>();

export type RouteConfig<RouteName> = {
  name: RouteName;
  path: string;
  component: React.ComponentType<unknown>;
  minRequiredRole?: UserRoleUI;
  redirectToIfNoPermission?: RouteName;
  initialParams?: unknown;
};

const routeConfigStudent = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Home",
      path: "home",
      component: withAuth(require("../views/HomeView").default),
    },
    {
      name: "Companion Home",
      path: "companion/home",
      component: withAuth(require("../views/CompanionHomeView").default),
    },
    {
      name: "Companion Chat",
      path: "companion/chat",
      component: withAuth(require("../views/CompanionView").default),
    },
    {
      name: "Portfolio",
      path: "portfolio",
      component: withAuth(require("../views/PortfolioView").default),
    },
    {
      name: "Journal",
      path: "journal",
      component: withAuth(require("../views/JournalView").default),
    },
    {
      name: "Coursework",
      path: "coursework",
      component: withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Activities",
      path: "activities",
      component: withAuth(require("../views/SchoolsView").default),
    },
    {
      name: "Course",
      path: "school/course",
      component: withAuth(require("../views/CourseView").default),
    },
    {
      name: "Custom School Student Courses",
      path: "student/school",
      component: withAuth(require("../views/CustomSchoolStudentCoursesView").default),
    },
    {
      name: "Custom Course Student",
      path: "student/school/course",
      component: withAuth(require("../views/CustomCourseStudentView").default),
    },
    {
      name: "Custom Resource Student",
      path: "student/school/course/resource",
      component: withAuth(require("../views/CustomResourceStudentView").default),
    },
    {
      name: "Custom Assignment Student",
      path: "student/school/course/assignment",
      component: withAuth(require("../views/CustomAssignmentStudentView").default),
    },
    {
      name: "Custom Course AITutor",
      path: "student/school/course/aitutor",
      component: withAuth(require("../views/CustomCourseAITutorChatView").default),
    },
    {
      name: "Custom Course Assessment",
      path: "student/school/course/assessment",
      component: withAuth(require("../views/CustomCourseAssessmentStudentView").default),
    },
    {
      name: "Learn",
      path: "learn",
      component: withAuth(require("../views/LearnView").default),
    },
    {
      name: "Tracks",
      path: "tracks",
      component: withAuth(require("../views/TracksView").default),
    },
    {
      name: "Track Overview",
      path: "track/overview",
      component: withAuth(require("../views/TrackOverviewView").default),
    },
    {
      name: "Categories",
      path: "categories",
      component: withAuth(require("../views/CategoriesView").default),
    },
    {
      name: "Lesson",
      path: "learn/lesson",
      component: withAuth(require("../views/LessonSubchapterView").default),
    },
    {
      name: "Lesson Activities",
      path: "learn/lesson/activities",
      component: withAuth(require("../views/LessonLearningView").default),
    },
    {
      name: "Lesson Assessment",
      path: "learn/lesson/assessment",
      component: withAuth(require("../views/LessonAssessmentView").default),
    },
    {
      name: "Lesson Chat",
      path: "learn/lesson/chat",
      component: withAuth(require("../views/LessonChatView").default),
    },
    {
      name: "Lesson Video",
      path: "learn/lesson/video",
      component: withAuth(require("../views/LessonVideoView").default),
    },
    {
      name: "Onboarding",
      path: "onboarding",
      component: withAuth(require("../views/OnboardingView").default),
    },
    {
      name: "Settings",
      path: "settings",
      component: withAuth(require("../views/SettingsView").default),
    },
    {
      name: "Invite",
      path: "invite",
      component: withAuth(require("../views/InviteView").default),
    },
    {
      name: "Career",
      path: "career",
      component: withAuth(require("../views/CareerView").default),
    },
    {
      name: "Degree",
      path: "degree",
      component: withAuth(require("../views/DegreeView").default),
    },
    {
      name: "Project",
      path: "project",
      component: withAuth(require("../views/ProjectView").default),
    },
    {
      name: "CareerDetails",
      path: "career/details",
      component: withAuth(require("../views/CareerDetailView").default),
    },
    {
      name: "ThankYou",
      path: "thank-you",
      component: withAuth(require("../views/ThankYouView").default),
    },
    {
      name: "Custom Module Student",
      path: "student/school/course/module",
      component: withAuth(require("../views/CustomModuleStudentView").default),
    },
    {
      name: "Resource",
      path: "school/course/resource",
      component: withAuth(require("../views/ResourceView").default),
    },
    {
      name: "School",
      path: "school",
      component: withAuth(require("../views/SchoolView").default),
    },
    {
      name: "Announcement",
      path: "announcement",
      component: withAuth(require("../views/AnnouncementView").default),
    },
    {
      name: "Announcement Detail",
      path: "announcement/detail",
      component: withAuth(require("../views/AnnouncementDetailView").default),
    },
    {
      name: "Enrollment",
      path: "student/school/course/enrollment",
      component: withAuth(require("../views/EnrollmentView").default),
    },
    {
      name: "Chat Settings",
      path: "companion/settings",
      component: withAuth(require("../views/ChatSettingsView").default),
    },
    {
      name: "To do",
      path: "companion/todo",
      component: withAuth(require("../views/TodoView").default),
    },
    {
      name: "Learning Activities",
      path: "companion/learning-activities",
      component: withAuth(require("../views/CompanionActivityView").default),
    },
    {
      name: "Interactive Learning Environment",
      path: "companion/learning-activity",
      component: withAuth(require("../views/LearningActivityView").default),
    },
    {
      name: "Insights",
      path: "student/insights",
      component: withAuth(require("../views/InsightsView").default),
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigFaculty = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Student Analytics",
      path: "student-analytics",
      component: withAuth(require("../views/StudentAnalyticsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Custom School Faculty Courses",
      path: "school/courses",
      component: withAuth(require("../views/CustomSchoolFacultyCoursesView").default),
      minRequiredRole: UserRoleUI.FACULTY,
      redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
    },
    {
      name: "Custom Coursework Modules",
      path: "school/course/modules",
      component: withAuth(require("../views/CustomCourseworkModulesView").default),
      minRequiredRole: UserRoleUI.FACULTY,
      redirectToIfNoPermission: !isCompanionMode ? "Custom Course Student" : "Companion Home",
    },
    {
      name: "Course Settings",
      path: "school/course/settings",
      component: withAuth(require("../views/CourseSettingsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
      redirectToIfNoPermission: !isCompanionMode ? "Custom School Student Courses" : "Companion Home",
    },
    {
      name: "Faculty",
      path: "faculty",
      component: withAuth(require("../views/FacultyView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Course Evaluator",
      path: "admin/course-evaluator",
      component: withAuth(require("../views/adminViews/CourseEvaluatorView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
    {
      name: "Course Evaluator Details",
      path: "admin/course-evaluator/details",
      component: withAuth(require("../views/adminViews/CourseEvaluatorDetailView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigAdmin = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Admin",
      path: "dashboard",
      component: withAuth(require("../views/AdminView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
    {
      name: "Create Tenant",
      path: "dashboard/tenant/create",
      component: withAuth(require("../views/CreateTenantView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
    {
      name: "Tenant Dashboard",
      path: "dashboard/tenant",
      component: withAuth(require("../views/TenantDashboardView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
    {
      name: "Tenant Settings",
      path: "dashboard/tenant/settings",
      component: withAuth(require("../views/TenantSettingsDashboardView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
    {
      name: "OAuth Client Dashboard",
      path: "dashboard/oauth-client",
      component: withAuth(require("../views/OAuthClientDashboardView").default),
      minRequiredRole: UserRoleUI.ADMIN,
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigSchoolAdmin = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    isCompanionMode
      ? {
          name: "Admin Dashboard",
          path: "admin/dashboard",
          component: withAuth(require("../views/adminViews/AICompanionDashboardView").default),
          minRequiredRole: UserRoleUI.FACULTY,
        }
      : {
          name: "Admin Dashboard",
          path: "admin/dashboard",
          component: withAuth(require("../views/adminViews/AdminDashboardView").default),
          minRequiredRole: UserRoleUI.FACULTY,
        },
    {
      name: "Manage Courses",
      path: "admin/manage-courses",
      component: withAuth(require("../views/adminViews/ManageCoursesView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Teaching",
      path: "admin/teaching",
      component: withAuth(require("../views/adminViews/FacultyTeachingView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Degrees & Programs",
      path: "admin/degrees-programs",
      component: withAuth(require("../views/adminViews/DegreesProgramsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Degree Information",
      path: "admin/degree-information",
      component: withAuth(require("../views/adminViews/DegreeInformationView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Course Information",
      path: "admin/course-information",
      component: withAuth(require("../views/adminViews/CourseInformationView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Students",
      path: "admin/students",
      component: withAuth(require("../views/adminViews/StudentsListView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Student Profile",
      path: "admin/students/profile",
      component: withAuth(require("../views/adminViews/StudentProfileView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Users",
      path: "admin/users",
      component: withAuth(require("../views/adminViews/AdminUsersView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Grading Center",
      path: "admin/grading-center",
      component: withAuth(require("../views/adminViews/GradingCenterView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Analytics",
      path: "admin/analytics",
      component: withAuth(require("../views/adminViews/AdminAnalyticsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Settings",
      path: "admin/settings",
      component: withAuth(require("../views/adminViews/AdminSettingsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Profile",
      path: "admin/profile",
      component: withAuth(require("../views/adminViews/AdminProfileView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Session Analytics",
      path: "session-analytics",
      component: withAuth(require("../views/adminViews/SessionAnalyticsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "User Session Details",
      path: "session-analytics/user",
      component: withAuth(require("../views/adminViews/UserSessionDetailsView").default),
      minRequiredRole: UserRoleUI.FACULTY,
    },
    {
      name: "Admin Announcements",
      path: "admin/announcements",
      component: withAuth(require("../views/adminViews/AdminAnnouncementsView").default),
      minRequiredRole: UserRoleUI.SCHOOL_ADMIN,
    },
  ] as const satisfies RouteConfig<RouteName>[];

const routeConfigAuth = (isCompanionMode: boolean, isLMS: boolean): RouteConfig<RouteName>[] =>
  [
    {
      name: "Login",
      path: "login",
      component: noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginSuperAdmin",
      path: "admin/login",
      component: noAuth(require("../views/LoginView").default),
      initialParams: { isLMS },
    },
    {
      name: "LoginLTI",
      path: "login/lti",
      component: noAuth(require("../views/LoginLTIView").default),
    },
    {
      name: "LoginSSO",
      path: "login/sso",
      component: noAuth(require("../views/LoginSSOView").default),
    },
    {
      name: "Signup",
      path: "signup",
      component: noAuth(require("../views/SignupView").default),
      initialParams: { isLMS },
    },
    {
      name: "Reset Password",
      path: "reset-password",
      component: noAuth(require("../views/ResetPasswordView").default),
    },
    {
      name: "Set Password",
      path: "set-password",
      component: require("../views/SetPasswordView").default,
    },
    {
      name: "Verify Email",
      path: "verify-email",
      component: noAuth(require("../views/VerifyEmailView").default),
    },
    {
      name: "EdlinkSuccess",
      path: "edlink-success",
      component: withAuth(require("../views/EdlinkSuccessView").default),
    },
  ] as const satisfies RouteConfig<RouteName>[];

export const routeConfig: (isCompanionMode: boolean, isLMS: boolean) => RouteConfig<RouteName>[] = (
  isCompanionMode: boolean,
  isLMS: boolean
) =>
  [
    ...routeConfigStudent(isCompanionMode, isLMS),
    ...routeConfigFaculty(isCompanionMode, isLMS),
    ...routeConfigAdmin(isCompanionMode, isLMS),
    ...routeConfigSchoolAdmin(isCompanionMode, isLMS),
    ...routeConfigAuth(isCompanionMode, isLMS),
  ] as const satisfies RouteConfig<RouteName>[];

function AppNavigator() {
  const { currentDomain, isLMS, isCompanionMode } = useAppConfig();
  const navigation = useRef();

  const { userInfo, isAuthenticated, isEmailVerified, otpVerifiedForSession, isAuthReady } = useAuth();

  const isVerified = isEmailVerified && otpVerifiedForSession;
  const initialRoute = useMemo<RouteName>(() => {
    if (!isAuthenticated) {
      return "Login";
    }
    if (!isVerified) {
      return "Verify Email";
    }
    return getDefaultAuthenticatedRoute({ userRole: userInfo.userRole, isCompanionMode });
  }, [isAuthenticated, isVerified, isCompanionMode, userInfo.userRole]);

  const routes = routeConfig(isCompanionMode, isLMS);

  const screens = {};
  for (const route of routes) {
    screens[route.name] = route.path;
  }

  const linking = {
    prefixes: [`https://${currentDomain}`, "primer://"],
    config: {
      screens: screens,
    },
  };

  if (!isAuthReady) {
    return (
      <View style={styles.loaderContainer}>
        <Loader />
      </View>
    );
  }

  return (
    <NavigationContainer
      linking={linking}
      ref={navigation}
      onReady={() => {
        routingInstrumentation.registerNavigationContainer(navigation);
      }}>
      <Stack.Navigator key={initialRoute} initialRouteName={initialRoute} screenOptions={{ headerShown: false }}>
        {routes.map((route) => (
          <Stack.Screen
            key={route.name}
            name={route.name as keyof RootStackParamList}
            getComponent={() => route.component}
            initialParams={route.initialParams}
          />
        ))}
      </Stack.Navigator>
    </NavigationContainer>
  );
}

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default AppNavigator;
